import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { checkEmailAvailability, registerPublicCustomer, resendPublicVerifyEmail } from '../../services/auth';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

type EmailCheckStatus = 'idle' | 'checking' | 'available' | 'unavailable' | 'invalid';

const RegisterPlaceholderPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailCheckStatus, setEmailCheckStatus] = useState<EmailCheckStatus>('idle');
  const [emailCheckText, setEmailCheckText] = useState('');
  const emailCheckSeqRef = useRef(0);
  const hasConfirmInput = confirmPassword.length > 0;
  const isPasswordMatched = hasConfirmInput && password === confirmPassword;
  const isPasswordMismatched = hasConfirmInput && password !== confirmPassword;

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = window.setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  useEffect(() => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setEmailCheckStatus('idle');
      setEmailCheckText('');
      return;
    }
    if (!normalizedEmail.includes('@')) {
      setEmailCheckStatus('invalid');
      setEmailCheckText('请输入有效邮箱格式');
      return;
    }
    setEmailCheckStatus('checking');
    setEmailCheckText('正在检查邮箱可用性...');
    const currentSeq = ++emailCheckSeqRef.current;
    const timer = window.setTimeout(async () => {
      try {
        const result = await checkEmailAvailability(normalizedEmail);
        if (currentSeq !== emailCheckSeqRef.current) return;
        if (result.available) {
          setEmailCheckStatus('available');
          setEmailCheckText(result.detail || '该邮箱可用');
        } else {
          setEmailCheckStatus('unavailable');
          setEmailCheckText(result.detail || '该邮箱已被占用');
        }
      } catch (err: any) {
        if (currentSeq !== emailCheckSeqRef.current) return;
        setEmailCheckStatus('unavailable');
        setEmailCheckText(err?.response?.data?.detail || '邮箱不可用，请更换后重试');
      }
    }, 400);
    return () => window.clearTimeout(timer);
  }, [email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setError('请输入邮箱');
      return;
    }
    if (password.length < 6) {
      setError('密码至少6位');
      return;
    }
    if (username.trim().length > 20) {
      setError('用户名长度不能超过20位');
      return;
    }
    if (password !== confirmPassword) {
      setError('两次输入的密码不一致');
      return;
    }
    try {
      const checkResult = await checkEmailAvailability(normalizedEmail);
      if (!checkResult.available) {
        setError(checkResult.detail || '该邮箱已被占用');
        setEmailCheckStatus('unavailable');
        setEmailCheckText(checkResult.detail || '该邮箱已被占用');
        return;
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || '邮箱不可用，请更换后重试');
      setEmailCheckStatus('unavailable');
      setEmailCheckText(err?.response?.data?.detail || '邮箱不可用，请更换后重试');
      return;
    }
    setLoading(true);
    try {
      const normalizedUsername = username.trim();
      const result = await registerPublicCustomer({
        email: normalizedEmail,
        password,
        username: normalizedUsername || undefined,
      });
      setSuccess(result.detail || '注册成功，请查收邮箱并完成验证');
      setPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err?.response?.data?.detail || '注册失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setSuccess('');
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setError('请先输入要重发的邮箱');
      return;
    }
    setResending(true);
    try {
      const result = await resendPublicVerifyEmail(normalizedEmail);
      setSuccess(result.detail || '验证邮件已发送，请查收邮箱');
      setResendCooldown(60);
    } catch (err: any) {
      setError(err?.response?.data?.detail || '重发失败，请稍后重试');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-2xl px-6 py-16">
        <div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
          <h1 className="text-2xl font-bold text-gray-900">客户注册</h1>
          <p className="mt-3 text-gray-600">注册并邮箱验证成功后才可登录;用户名为选填</p>

          {error ? <div className="mt-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}
          {success ? <div className="mt-4 rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">{success}</div> : null}

          <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
            <div>
              <label className="mb-1 block text-sm text-gray-700">
                邮箱 <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="请输入邮箱"
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                disabled={loading}
                required
              />
              {emailCheckText ? (
                <p
                  className={`mt-1 text-xs ${
                    emailCheckStatus === 'available'
                      ? 'text-green-600'
                      : emailCheckStatus === 'checking'
                        ? 'text-gray-500'
                        : 'text-red-600'
                  }`}
                >
                  {emailCheckText}
                </p>
              ) : null}
            </div>
            <div>
              <label className="mb-1 block text-sm text-gray-700">
                用户名 <span className="text-gray-400">(选填)</span>
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="允许中文/英文字母/数字/下划线及@.+-; 不允许空格和其他特殊字符（最多20位）"
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                disabled={loading || resending}
                maxLength={20}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm text-gray-700">
                密码 <span className="text-red-500">*</span>
              </label>
              <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="至少6位"
                className="w-full rounded border border-gray-300 px-3 py-2 pr-10 text-sm outline-none focus:border-blue-500"
                disabled={loading}
                required
              />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  aria-label={showPassword ? '隐藏密码' : '显示密码'}
                >
                  {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm text-gray-700">
                确认密码 <span className="text-red-500">*</span>
              </label>
              <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="再次输入密码"
                className="w-full rounded border border-gray-300 px-3 py-2 pr-10 text-sm outline-none focus:border-blue-500"
                disabled={loading}
                required
              />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  aria-label={showConfirmPassword ? '隐藏确认密码' : '显示确认密码'}
                >
                  {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                </button>
              </div>
              {isPasswordMismatched ? (
                <p className="mt-1 text-xs text-red-600">两次输入的密码不一致</p>
              ) : isPasswordMatched ? (
                <p className="mt-1 text-xs text-green-600">两次输入的密码一致</p>
              ) : null}
            </div>
            <button
              type="submit"
              disabled={loading || resending}
              className="w-full rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? '提交中...' : '注册并发送验证邮件'}
            </button>
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-gray-500">没收到验证邮件?</span>
              <button
                type="button"
                onClick={handleResend}
                disabled={loading || resending || resendCooldown > 0}
                className="rounded border border-blue-300 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {resending ? '重发中...' : resendCooldown > 0 ? `重发验证邮件（${resendCooldown}s）` : '重发验证邮件'}
              </button>
            </div>
          </form>

          <div className="mt-8 flex gap-3">
            <Link
              to="/login"
              className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              去登录
            </Link>
            <Link
              to="/"
              className="rounded border border-blue-300 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50"
            >
              返回首页
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPlaceholderPage;

